import * as RadixPopover from "@radix-ui/react-popover";
import clsx from "clsx";
import { Command } from "cmdk";
import { ReactNode } from "react";
import { Divider } from "../../divider/Divider";
import { Icon } from "../../icon/Icon";
import * as selectStyles from "./Select.css";

/**
 * TODO: Address Popover theming issue. See related ticket: https://bestjira.atlassian.net/browse/DAS-4236
 */

export const POSITION = "bottom";
export const LOADING_TEXT = "Loading...";
export const SEARCH_PLACEHOLDER = "Search...";
export const NO_RESULTS_TEXT = "No results found.";

export interface Option {
  /**
   * The label to display for the option.
   */
  label: string;
  /**
   * The value of the option.
   */
  value: string;
  /**
   * Whether the option is relevant for "select relevant" functionality.
   */
  relevant?: boolean;
}

/**
 * DropdownPosition defines the possible positions for the dropdown popover.
 */
export type DropdownPosition = "top" | "bottom" | "left" | "right";

export type CommandFilterFn = (
  /**
   * value to filter by
   */
  value: string,
  /**
   * search string to filter against
   */
  search: string,
  /**
   * Optional keywords to filter by
   */
  keywords?: string[],
  /**
   * Optional metadata for the option
   */
  optionMeta?: Record<string, unknown>
) => number;

export type RenderTriggerFn = (
  /**
   * The value to display in the trigger.
   */
  value: string,
  /**
   * Placeholder text to show when value is empty.
   */
  placeholder?: string
) => ReactNode;

export type RenderItemFn = (item: string, isSelected: boolean) => ReactNode;

/**
 * CommonSelectProps defines shared props for select components.
 */
export interface CommonSelectProps {
  /**
   *  Placeholder text when no value is selected.
   */
  placeholder?: string;
  /**
   *  Whether the select is disabled.
   */
  disabled?: boolean;
  /**
   * Position of the dropdown popover.
   */
  position?: DropdownPosition;
  /**
   * Whether the select is searchable.
   */
  searchable?: boolean;
  /**
   *  Placeholder text for the search input.
   */
  searchPlaceholder?: string;
  /**
   * Text to display when no results are found.
   */
  noResultsText?: string;
  /**
   * Whether the select is in a loading state.
   */
  loading?: boolean;
  /**
   * Text to display while loading.
   */
  loadingText?: string;
  /**
   * Whether to use the default filtering logic.
   */
  shouldFilter?: boolean;
  /**
   * Custom filter function for options.
   */
  filter?: CommandFilterFn;
  /**
   * Handler for key down events on the search input.
   */
  handleInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * SelectBaseProps defines props for the SelectBase component.
 */
export interface SelectBaseProps extends CommonSelectProps {
  /**
   * Whether the popover is open.
   */
  isPopoverOpen: boolean;
  /**
   * Content to render in the trigger button.
   */
  triggerContent: ReactNode;
  /**
   * Content to render inside the popover.
   */
  popoverContent: ReactNode;

  /**
   * Function to set the popover open state.
   */
  setIsPopoverOpen: (open: boolean) => void;
  /**
   * Handler to toggle the popover open/close.
   */
  handleTogglePopover: () => void;
  /**
   * Optional content to render in the popover footer.
   */
  footerContent?: ReactNode;
  /**
   *  Whether to show the clear all items option.
   */
  clearAll?: boolean;
  /**
   * Callback to clearAllSelection.
   */
  clearAllSelection?: () => void;
}

export function SelectBase({
  disabled,
  isPopoverOpen,
  loading = false,
  shouldFilter = true,
  searchable = true,
  position = POSITION,
  loadingText = LOADING_TEXT,
  searchPlaceholder = SEARCH_PLACEHOLDER,
  noResultsText = NO_RESULTS_TEXT,
  triggerContent,
  popoverContent,
  footerContent,
  clearAll = false,
  filter,
  handleTogglePopover,
  setIsPopoverOpen,
  handleInputKeyDown,
  clearAllSelection
}: SelectBaseProps): React.ReactElement {
  return (
    /* Root Popover: Controls open/close state and modal behavior */
    <RadixPopover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      {/* Trigger Button: Opens/closes the popover */}
      <RadixPopover.Trigger asChild>
        <button
          type="button"
          onClick={handleTogglePopover}
          className={selectStyles.selectTrigger}
          disabled={disabled}
          aria-expanded={isPopoverOpen}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleTogglePopover();
            }
          }}
        >
          {/* Trigger Content (selected value or placeholder) */}
          {triggerContent}
          {/* Chevron Icon indicates dropdown state */}
          <div className={selectStyles.selectButtonLayout}>
            {clearAll && (
              <>
                <span
                  onClick={(event) => {
                    event.stopPropagation();
                    clearAllSelection?.();
                  }}
                  className={selectStyles.closeButtonWrapper}
                >
                  <Icon name="close" />
                </span>
                <div className={selectStyles.separator}>
                  <Divider orientation="vertical" />
                </div>
              </>
            )}
            <Icon name={isPopoverOpen ? "chevron-up" : "chevron-down"} />
          </div>
        </button>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        {/* Popover Content: Dropdown panel */}
        <RadixPopover.Content
          align="start"
          side={position}
          sideOffset={5}
          className={clsx(
            selectStyles.selectPopover,
            selectStyles.selectPortalRoot
          )}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          role="dialog"
          aria-label="Select dropdown"
        >
          {/* Command Pattern: Handles search/filter/list */}
          <Command shouldFilter={shouldFilter} filter={filter}>
            {/* Search Input (if enabled and not loading) */}
            {searchable && !loading && (
              <div className={selectStyles.selectSearchWrapper}>
                <Icon name="search" />
                <Command.Input
                  className={selectStyles.selectSearchInput}
                  placeholder={searchPlaceholder}
                  autoFocus
                  onKeyDown={handleInputKeyDown}
                />
              </div>
            )}
            {/* List of options */}
            <Command.List className={selectStyles.selectList} role="listbox">
              {loading ? (
                // Loading State
                <Command.Loading
                  className={selectStyles.selectListSecondaryState}
                >
                  {loadingText}
                </Command.Loading>
              ) : (
                <>
                  {/* Empty State (no results) */}
                  <Command.Empty>
                    <div className={selectStyles.selectListSecondaryState}>
                      {noResultsText}
                    </div>
                  </Command.Empty>
                  {/* Actual options/content */}
                  {popoverContent}
                </>
              )}
            </Command.List>
            {/* Optional Footer Content */}
            {footerContent}
          </Command>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
